import { useEffect, useRef, useState } from "react";
import { SearchIcon, BellIcon } from "@heroicons/react/solid";
import { NavLink, useNavigate } from "react-router-dom";
import { safeGetJSON } from "../../utils/storage";
import { useAuth } from "../../hooks/useAuth";
import logo from "../../assets/abroadjacks_logo.png";

const statCards = [
  { label: "Total Offers", value: "12", icon: "🎁", gradient: "from-indigo-500 to-purple-500" },
  { label: "Total Leads", value: "86", icon: "📥", gradient: "from-sky-500 to-blue-500" },
  { label: "Profile Views", value: "1.2K", icon: "👀", gradient: "from-emerald-500 to-lime-500" },
];

const ownerLinks = [
  { to: "/owner/dashboard", label: "Dashboard" },
  { to: "/owner/add-offer", label: "Offers" },
  { to: "/owner/leads", label: "Leads" },
  { to: "/owner/settings", label: "Settings" },
];

export default function OwnerHeader({ onToggleSidebar }) {
  const user = safeGetJSON("user") || {};
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    const handler = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <header className="px-4 py-3 sm:px-6 sm:py-4">
      <div className="flex flex-col gap-4 rounded-2xl border border-white/10 bg-gradient-to-r from-slate-900/70 via-blue-900/60 to-slate-900/70 px-4 py-4 shadow-[0_15px_60px_rgba(15,23,42,0.7)] backdrop-blur sm:rounded-3xl sm:px-5 lg:flex-row lg:items-center lg:justify-between">
        {/* Left brand block */}
        <div className="flex items-center gap-4">
          <button
            onClick={onToggleSidebar}
            className="hidden rounded-full border border-white/10 bg-white/5 p-2 transition hover:bg-white/10 lg:inline-flex"
            aria-label="Toggle sidebar"
          >
            <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16"/>
            </svg>
          </button>
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-blue-500/50 blur-xl"></div>
            <img
              src={logo}
              alt="AbroadJacks"
              className="relative h-12 w-auto rounded-full border-[2px] border-white/70 shadow-lg"
            />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[4px] text-white/50 sm:text-sm sm:tracking-[6px]">AbroadJacks</p>
            <h2 className="text-lg font-semibold text-white sm:text-xl">Owner Control Panel</h2>
          </div>
          <div className="hidden md:flex items-center bg-white/10 border border-white/20 rounded-2xl px-3 py-2 gap-2 ml-6">
            <SearchIcon className="h-4 w-4 text-white/60" />
            <input
              placeholder="Search offers, leads..."
              className="bg-transparent outline-none text-sm text-white placeholder-white/60"
            />
          </div>
        </div>

        {/* Right profile block */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-4 items-center justify-end">
            <button className="p-3 rounded-full bg-white/5 border border-white/10 text-white relative">
              <BellIcon className="h-5 w-5" />
              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-rose-400" />
            </button>

            <div
              className="relative"
              ref={dropdownRef}
            >
              <button
                onClick={() => setOpen((prev) => !prev)}
              className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/5 px-3 py-2.5 shadow-lg transition hover:-translate-y-1 sm:gap-4 sm:rounded-3xl sm:px-4 sm:py-3"
              >
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-blue-500/40 blur-lg" />
                  <div className="relative flex h-11 w-11 items-center justify-center rounded-full border border-white/30 text-base font-semibold text-white sm:h-14 sm:w-14 sm:text-lg">
                    {(user.name?.charAt(0) || "A").toUpperCase()}
                  </div>
                </div>
                <div className="min-w-0 text-left">
                  <p className="max-w-[150px] truncate text-sm font-semibold text-white sm:max-w-none sm:text-lg">
                    {user.consultancyName || "Consultancy"}
                  </p>
                  <p className="text-white/70 text-sm">{user.name || "Owner"}</p>
                  <div className="mt-1 hidden gap-2 sm:flex">
                    <span className="px-2 py-0.5 text-[10px] rounded-full bg-white/10 border border-white/20 uppercase tracking-[3px]">
                      Consultancy Owner
                    </span>
                    <span className="px-2 py-0.5 text-[10px] rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 uppercase tracking-[3px]">
                      Active ✅
                    </span>
                    <span className="px-2 py-0.5 text-[10px] rounded-full bg-indigo-500/20 text-indigo-200 border border-indigo-400/40 uppercase tracking-[3px]">
                      Pro Member
                    </span>
                  </div>
                </div>
              </button>
              {open && (
                <div className="absolute right-0 mt-3 w-60 rounded-2xl border border-white/10 bg-slate-900/90 backdrop-blur shadow-2xl">
                  <button
                    onClick={() => navigate(`/consultancy/${user.consultancyId || ""}`)}
                    className="w-full text-left px-4 py-3 text-sm text-white hover:bg-white/10"
                  >
                    My Public Profile
                  </button>
                  <button
                    onClick={() => navigate("/owner/settings")}
                    className="w-full text-left px-4 py-3 text-sm text-white hover:bg-white/10"
                  >
                    Account Settings
                  </button>
                  <button
                    onClick={() => {
                      logout();
                      navigate("/", { replace: true });
                    }}
                    className="w-full text-left px-4 py-3 text-sm text-rose-200 hover:bg-white/10"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {statCards.map((card) => (
              <div
                key={card.label}
                className={`rounded-2xl bg-gradient-to-br ${card.gradient} p-3 text-white shadow-lg hover:-translate-y-1 transition`}
              >
                <div className="text-2xl">{card.icon}</div>
                <p className="text-xl font-bold mt-1">{card.value}</p>
                <p className="text-xs uppercase tracking-[3px] text-white/70">
                  {card.label}
                </p>
              </div>
            ))}
          </div>
        </div>

        <nav className="grid grid-cols-2 gap-2 border-t border-white/10 pt-3 sm:grid-cols-4 lg:hidden" aria-label="Owner mobile navigation">
          {ownerLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                `min-h-[44px] rounded-xl px-3 py-2 text-center text-sm font-semibold transition ${
                  isActive ? "bg-white/15 text-white" : "bg-white/[0.06] text-white/75 hover:bg-white/10"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  );
}
